// 모집중 상세 내역
import {
  Box,
  Flex,
  Text,
  Button,
  Switch,
  HStack,
  VStack,
  useDisclosure,
  useColorModeValue,
  AspectRatio,
  Checkbox,
  Radio,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import CreatorList from "../../components/CreatorList";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { cpsCampaignProductsView, pendingPaymentDetail } from "../../api";
import CampaignVisibilityControl from "../../components/CampaignVisibilityControl";

export default function PendingPaymentDetail() {
  // 섹션 접기/펼치기 상태
  const basic = useDisclosure({ defaultIsOpen: true });
  const product = useDisclosure({ defaultIsOpen: false });
  const target = useDisclosure({ defaultIsOpen: false });
  const content = useDisclosure({ defaultIsOpen: false });
  const guide = useDisclosure({ defaultIsOpen: false });

  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("#E2E8F0", "gray.700");
  const headBg = useColorModeValue("white", "gray.700");

  const [campaign, setCampaign] = useState(null);
  const [creator, setCreator] = useState([]);
  const [recommendCreators, setRecommendCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cpsProducts, setCpsProducts] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();

  // 제안서 상세 조회 API 연동
  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        setLoading(true);
        const res = await pendingPaymentDetail({ id });

        setCampaign(res.data.campaign);
        setCreator(res.data.appliedCreators);
        setRecommendCreators(res.data.recommendCreators);
        const model = String(
          res?.data?.campaign?.billingModel ?? res?.data?.campaign?.type ?? ""
        ).toUpperCase();
        if (model === "CPS") {
          try {
            const cpsRes = await cpsCampaignProductsView({ campaignId: id });
            setCpsProducts(
              Array.isArray(cpsRes) ? cpsRes : cpsRes?.data ?? []
            );
          } catch {
            setCpsProducts([]);
          }
        } else {
          setCpsProducts([]);
        }
      } catch (e) {
        console.error("proposalDetail error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <Flex h="300px" align="center" justify="center">
        로딩 중...
      </Flex>
    );
  }

  if (!campaign) {
    return (
      <Flex h="300px" align="center" justify="center" color="gray.500">
        데이터를 불러올 수 없습니다.
      </Flex>
    );
  }

  const visibilityTag = campaign?.deletedAt
    ? { label: "숨김", bg: "#E53E3E" }
    : { label: "노출", bg: "#38A169" };

  return (
    <Box>
      {/* 컨테이너 */}
      <Box mx="auto">
        {/* 페이지 헤더 */}
        <Flex mb="20px" justify="space-between" align="flex-start" gap="16px">
          <Box flex="1" minW="0">
            <Flex align="center" gap="8px" mb="2" wrap="wrap">
              <Text fontSize="xl" fontWeight="bold">
                {campaign.title}
              </Text>
              <Box
                bg={visibilityTag.bg}
                color="white"
                px="10px"
                py="3px"
                fontSize="12px"
                borderRadius="8px"
                fontWeight="700"
              >
                {visibilityTag.label}
              </Box>
            </Flex>
            <Text color="gray.600">{campaign.description}</Text>
          </Box>
          <Button
            size="md"
            variant="outline"
            onClick={() => navigate("/suggest/pendingpayment")}
          >
            목록으로
          </Button>
          <CampaignVisibilityControl
            campaignId={campaign?.campaignId ?? campaign?.id ?? id}
            hidden={Boolean(campaign?.deletedAt)}
            onChanged={(nextHidden) =>
              setCampaign((prev) =>
                prev
                  ? {
                      ...prev,
                      deletedAt: nextHidden ? new Date().toISOString() : null,
                    }
                  : prev
              )
            }
          />
          <Button
            size="md"
            variant="outline"
            colorScheme="blue"
            flexShrink={0}
            onClick={() =>
              navigate(`/suggest/campaign/edit/${id}`, {
                state: { campaign, source: "pending_payment" },
              })
            }
          >
            수정
          </Button>
        </Flex>

        <Flex gap="40px" pb="40px">
          {/* 좌측 폼 영역 */}
          <VStack w="50%" align="stretch" spacing="16px">
            {/* 기본 정보 */}
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={border}
              rounded="20px"
              boxShadow="sm"
              overflow="hidden"
              p="30px"
            >
              <Flex
                mb="30px"
                bg={headBg}
                align="center"
                justify="space-between"
                cursor="pointer"
                onClick={basic.onToggle}
              >
                <Text fontWeight="700" color="#0054C0" fontSize="24px">
                  기본 정보
                </Text>
                {basic.isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </Flex>

              {basic.isOpen && (
                <Box>
                  <Flex flexDir="column" gap="20px">
                    <Flex gap="20px">
                      <Field label="캠페인명" value={campaign.title} />
                      <Field label="인원" value={campaign.headcount} />
                    </Flex>

                    <Box>
                      <Text mb="6px" fontSize="16px" fontWeight={400}>
                        캠페인 설명
                      </Text>
                      <Box p="12px" borderRadius="8px" bgColor="#F4F7FA">
                        {campaign.description}
                      </Box>
                    </Box>
                    <Flex flexDir="column">
                      <Text fontSize="16px" fontWeight="400" mb="6px">
                        산업 카테고리
                      </Text>
                      <Flex
                        minH="44px"
                        gap="6px"
                        border="1px solid #D1D4D9"
                        p="12px"
                        borderRadius="6px"
                        overflowX="auto"
                        overflowY="hidden"
                        css={{
                          "&::-webkit-scrollbar": { display: "none" },
                          scrollbarWidth: "none",
                        }}
                      >
                        {campaign?.industryCategoryNames?.map(
                          (category, idx) => (
                            <Box
                              key={idx}
                              px="8px"
                              py="2px"
                              bg="#E9F2FF"
                              color="#4B5563"
                              fontSize="12px"
                              fontWeight={500}
                              borderRadius="6px"
                              flexShrink={0}
                            >
                              {category}
                            </Box>
                          )
                        )}
                      </Flex>
                    </Flex>

                    <Flex flexDir="column">
                      <Text fontSize="16px" fontWeight="400" mb="6px">
                        라이프스타일 카테고리
                      </Text>
                      <Flex
                        minH="44px"
                        gap="6px"
                        border="1px solid #D1D4D9"
                        p="12px"
                        borderRadius="6px"
                        overflowX="auto"
                        overflowY="hidden"
                        css={{
                          "&::-webkit-scrollbar": { display: "none" },
                          scrollbarWidth: "none",
                        }}
                      >
                        {campaign?.lifestyleCategoryNames?.map(
                          (category, idx) => (
                            <Box
                              key={idx}
                              px="8px"
                              py="2px"
                              bg="#E9F2FF"
                              color="#4B5563"
                              fontSize="12px"
                              fontWeight={500}
                              borderRadius="6px"
                              flexShrink={0}
                            >
                              {category}
                            </Box>
                          )
                        )}
                      </Flex>
                    </Flex>

                    <Box>
                      <Text mb="6px" fontWeight={400} fontSize="16px">
                        크리에이터 모집 예상 기간
                      </Text>
                      <HStack>
                        <Box
                          w="100%"
                          p="12px"
                          borderRadius="8px"
                          bgColor="#F4F7FA"
                        >
                          {campaign.recruitStart}
                        </Box>
                        <Text>~</Text>
                        <Box
                          w="100%"
                          p="12px"
                          borderRadius="8px"
                          bgColor="#F4F7FA"
                        >
                          {campaign.recruitEnd}
                        </Box>
                      </HStack>
                    </Box>

                    <Box>
                      <Text mb="6px" fontWeight={400} fontSize="16px">
                        캠페인 예상 실행 일자
                      </Text>
                      <HStack>
                        <Box
                          w="100%"
                          p="12px"
                          borderRadius="8px"
                          bgColor="#F4F7FA"
                        >
                          {campaign.runStart}
                        </Box>
                        <Text>~</Text>
                        <Box
                          w="100%"
                          p="12px"
                          borderRadius="8px"
                          bgColor="#F4F7FA"
                        >
                          {campaign.runStart}
                        </Box>
                      </HStack>
                    </Box>
                  </Flex>
                </Box>
              )}
            </Box>

            {/* 타겟 정보 */}
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={border}
              rounded="20px"
              boxShadow="sm"
              overflow="hidden"
              p="30px"
            >
              <Flex
                mb="30px"
                bg={headBg}
                align="center"
                justify="space-between"
                cursor="pointer"
                onClick={target.onToggle}
              >
                <Text fontWeight="700" color="#0054C0" fontSize="24px">
                  타겟 정보
                </Text>
                {target.isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </Flex>

              {target.isOpen && (
                <Box>
                  <Flex gap="20px" mb="20px">
                    <Field label="국가" value={campaign.country} />
                    <Field label="언어" value={campaign.language} />
                    <Field
                      label="성별"
                      value={
                        campaign?.gender === "male"
                          ? "남성"
                          : campaign?.gender === "female"
                          ? "여성"
                          : "기타"
                      }
                    />
                  </Flex>

                  <Flex gap="20px" mb="20px">
                    <Field label="최소 연령" value={campaign.ageMin} />
                    <Field label="최대 연령" value={campaign.ageMax} />
                  </Flex>

                  <Box>
                    <Text mb="6px" fontSize="16px" fontWeight={400}>
                      채널
                    </Text>
                    <Flex justifyContent="space-between">
                      <Checkbox
                        isChecked={campaign.channels?.includes("instagram")}
                      >
                        instagram
                      </Checkbox>

                      <Checkbox
                        isChecked={campaign.channels?.includes("youtube")}
                      >
                        youtube
                      </Checkbox>

                      <Checkbox
                        isChecked={campaign.channels?.includes("tiktok")}
                      >
                        tiktok
                      </Checkbox>

                      <Checkbox
                        isChecked={campaign.channels?.includes("facebook")}
                      >
                        facebook
                      </Checkbox>
                    </Flex>
                  </Box>
                </Box>
              )}
            </Box>

            {/* 콘텐츠 설정 */}
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={border}
              rounded="20px"
              boxShadow="sm"
              overflow="hidden"
              p="30px"
            >
              <Flex
                mb="30px"
                bg={headBg}
                align="center"
                justify="space-between"
                cursor="pointer"
                onClick={content.onToggle}
              >
                <Text fontWeight="700" color="#0054C0" fontSize="24px">
                  콘텐츠 설정
                </Text>
                {content.isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </Flex>

              {content.isOpen && (
                <Box>
                  <Box mb="20px">
                    <Text mb="6px" fontSize="16px" fontWeight={400}>
                      크리에이터 최소 팔로워
                    </Text>
                    <HStack>
                      <Switch
                        colorScheme="blue"
                        isChecked={campaign.allowAnyFollowers}
                      />
                      <Text fontSize="16px" fontWeight={400} color="#2C3136">
                        품질 수치 확인없이 참여 허용
                      </Text>
                    </HStack>
                  </Box>

                  <Box mb="20px">
                    <Text mb="6px" fontWeight={400} fontSize="16px">
                      콘텐츠 T&M
                    </Text>
                    <HStack spacing="16px">
                      <Radio w="50%" defaultChecked>
                        영상
                      </Radio>
                      <Radio w="50%">정보 전달</Radio>
                    </HStack>
                  </Box>

                  <Flex gap="20px" mb="20px">
                    <Field label="과금 모델" value={campaign.billingModel} />
                    <Field
                      label="총 예산"
                      value={campaign.totalBudget?.toLocaleString()}
                    />
                  </Flex>
                </Box>
              )}
            </Box>
          </VStack>

          {/* 우측 사이드 영역 */}
          <VStack w="50%" align="stretch" spacing="16px">
            {/* 제품 정보 */}
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={border}
              rounded="20px"
              boxShadow="sm"
              overflow="hidden"
              p="30px"
            >
              <Flex
                mb="30px"
                bg={headBg}
                align="center"
                justify="space-between"
                cursor="pointer"
                onClick={product.onToggle}
              >
                <Text fontWeight="700" color="#0054C0" fontSize="24px">
                  {String(campaign?.billingModel ?? campaign?.type ?? "").toUpperCase() ===
                  "CPS"
                    ? "상품 정보"
                    : "제품 정보"}
                </Text>
                {product.isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </Flex>

              {product.isOpen && (
                <Box>
                  <Text
                    mb="10px"
                    fontWeight={700}
                    color="#455466"
                    fontSize="18px"
                  >
                    브랜드 정보
                  </Text>
                  <Flex mb="20px" flexDir="column" gap="20px">
                    <Field label="브랜드명" value={campaign.brandName} />
                    <Box>
                      <Text mb="6px" fontSize="16px" fontWeight={400}>
                        브랜드 소개서
                      </Text>
                      <FileDownload
                        label="제품 영상"
                        fileName={campaign.brandPdfName}
                        fileUrl={campaign.brandPdfUrl}
                      />
                    </Box>
                  </Flex>

                  <AspectRatio
                    h="264px"
                    ratio={16 / 9}
                    mb="20px"
                    rounded="12px"
                    overflow="hidden"
                    bg="gray.100"
                  >
                    <video
                      src={campaign.brandMediaUrl}
                      controls
                      style={{ width: "100%", height: "100%" }}
                    />
                  </AspectRatio>

                  {String(
                    campaign?.billingModel ?? campaign?.type ?? ""
                  ).toUpperCase() === "CPS" ? (
                    <Flex gap="20px" flexDir="column">
                      {cpsProducts.length === 0 && (
                        <Text color="gray.500">등록된 판매상품이 없습니다.</Text>
                      )}
                      {cpsProducts.map((it) => {
                        const thumbs =
                          (it.thumbnailImageUrls ?? []).length > 0
                            ? it.thumbnailImageUrls
                            : (it.imageUrls ?? []).slice(0, 1);
                        const details =
                          (it.detailImageUrls ?? []).length > 0
                            ? it.detailImageUrls
                            : (it.imageUrls ?? []).slice(1);
                        return (
                          <Box key={it.cpsProductId}>
                            <Flex gap="20px" mb="20px">
                              <Field label="상품명" value={it.name} />
                              <Field
                                label="가격"
                                value={`${it.currency} ${Number(
                                  it.price ?? 0
                                ).toLocaleString()}`}
                              />
                            </Flex>
                            <Flex gap="20px" mb="20px">
                              <Field label="재고수량" value={it.stockQuantity} />
                              <Field label="상태" value={it.status} />
                            </Flex>
                            <Flex gap="20px" mb="20px">
                              <Field
                                label="커미션율"
                                value={
                                  it.commissionRate == null
                                    ? "-"
                                    : `${(Number(it.commissionRate) * 100).toFixed(
                                        2
                                      )}%`
                                }
                              />
                              <Field
                                label="플랫폼 수수료율"
                                value={
                                  it.platformFeeRate == null
                                    ? "-"
                                    : `${(Number(it.platformFeeRate) * 100).toFixed(
                                        2
                                      )}%`
                                }
                              />
                            </Flex>
                            <ImageList label="썸네일 이미지" urls={thumbs} />
                            <ImageList label="상세 이미지" urls={details} />
                          </Box>
                        );
                      })}
                    </Flex>
                  ) : (
                    <Flex gap="20px" flexDir="column">
                      <Text
                        mb="10px"
                        fontWeight={700}
                        color="#455466"
                        fontSize="18px"
                      >
                        제품 정보
                      </Text>
                      <Flex gap="20px" mb="20px">
                        <Field label="제품명" value={campaign.productName} />
                        <Field label="제품 링크" value={campaign.productLink} />
                      </Flex>
                      <Flex gap="20px" mb="20px">
                        <Field
                          label="제품 통화"
                          value={campaign?.productCurrency}
                        />
                        <Field label="제품 가격" value={campaign?.productPrice} />
                      </Flex>
                      <Field
                        label="제품 설명"
                        value={campaign?.productDescription}
                      />
                      <FileDownload
                        label="제품 이미지"
                        fileName={campaign.productImageName}
                        fileUrl={campaign.productImageUrl}
                      />

                      <FileDownload
                        label="제품 소개서"
                        fileName={campaign.productBrochureName}
                        fileUrl={campaign.productBrochureUrl}
                      />

                      <FileDownload
                        label="제품 영상"
                        fileName={campaign.productVideoName}
                        fileUrl={campaign.productVideoUrl}
                      />
                    </Flex>
                  )}
                </Box>
              )}
            </Box>

            {/* 제작 가이드 */}
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={border}
              rounded="20px"
              boxShadow="sm"
              overflow="hidden"
              p="30px"
            >
              <Flex
                mb="30px"
                bg={headBg}
                align="center"
                justify="space-between"
                cursor="pointer"
                onClick={guide.onToggle}
              >
                <Text fontWeight="700" color="#0054C0" fontSize="24px">
                  제작 가이드
                </Text>
                {guide.isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </Flex>

              {guide.isOpen && (
                <Box>
                  <Box mb="20px">
                    <Text mb="6px" fontWeight={400} fontSize="16px">
                      필수 에셋
                    </Text>
                    <Flex justifyContent="space-between">
                      <Checkbox
                        isChecked={campaign.requiredAssets?.includes("image")}
                      >
                        이미지
                      </Checkbox>
                      <Checkbox
                        isChecked={campaign.requiredAssets?.includes("video")}
                      >
                        영상
                      </Checkbox>
                      <Checkbox
                        isChecked={campaign.requiredAssets?.includes("text")}
                      >
                        텍스트
                      </Checkbox>
                      <Checkbox
                        isChecked={campaign.requiredAssets?.includes("link")}
                      >
                        링크
                      </Checkbox>
                      <Checkbox
                        isChecked={campaign.requiredAssets?.includes("coupon")}
                      >
                        쿠폰
                      </Checkbox>
                    </Flex>
                  </Box>

                  <Box mb="20px">
                    <Text mb="6px" fontWeight={400} fontSize="16px">
                      크리에이터 가이드
                    </Text>
                    <Box p="12px" borderRadius="8px" bgColor="#F4F7FA">
                      {campaign.creativeGuide}
                    </Box>
                  </Box>

                  <Flex gap="20px" flexDir="column">
                    <FileDownload
                      label="가이드 시안 업로드"
                      fileName={campaign.guideMockName}
                      fileUrl={campaign.guideMockUrl}
                    />
                    <Field
                      label="레퍼런스 링크"
                      value={campaign.referenceLink}
                    />
                  </Flex>
                </Box>
              )}
            </Box>
          </VStack>
        </Flex>
      </Box>
      {/* 신청 크리에이터 컴포넌트 */}
      <CreatorList
        page="pending_payment"
        creator={creator}
        recommendCreator={recommendCreators}
        data={campaign}
      />
    </Box>
  );
}

function Field({ label, value }) {
  return (
    <Box w="100%">
      <Text mb="6px" fontSize="16px" fontWeight={400}>
        {label}
      </Text>
      <Box p="12px" bgColor="#F4F7FA" borderRadius="8px">
        {value}
      </Box>
    </Box>
  );
}

function FileDownload({ label, fileName, fileUrl, onClickDownload }) {
  const handlePreview = () => {
    if (onClickDownload) return onClickDownload();

    // 새 탭에서 미리보기
    window.open(fileUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Box>
      {label && (
        <Text color="#172940" fontWeight="400" fontSize="16px" mb="6px">
          {label}
        </Text>
      )}

      <Flex
        h="40px"
        px="14px"
        border="1px solid #E2E8F0"
        borderRadius="10px"
        align="center"
        justify="space-between"
        bg="white"
      >
        <Text color="#4B5563" fontSize="14px" fontWeight={500} noOfLines={1}>
          {fileName || "파일 없음"}
        </Text>

        {fileUrl && (
          <Text
            color="#0054C0"
            fontSize="14px"
            fontWeight={600}
            cursor="pointer"
            onClick={handlePreview}
            _hover={{ textDecoration: "underline" }}
          >
            미리보기
          </Text>
        )}
      </Flex>
    </Box>
  );
}

function ImageList({ label, urls = [] }) {
  return (
    <Box mb="16px">
      <Text mb="6px" fontSize="16px" fontWeight={400}>
        {label}
      </Text>
      {urls.length === 0 ? (
        <Text color="gray.500" fontSize="14px">
          등록된 이미지가 없습니다.
        </Text>
      ) : (
        <Flex gap="8px" wrap="wrap">
          {urls.map((u, idx) => (
            <Box
              key={`${label}-${idx}`}
              w="96px"
              h="96px"
              overflow="hidden"
              borderRadius="8px"
              bg="#E5E7EB"
            >
              <img
                src={u}
                alt={`${label}-${idx + 1}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          ))}
        </Flex>
      )}
    </Box>
  );
}
